import { useEffect, useState } from "react";
import { GaugeChart } from "../slides/GaugeChart";

const SHOTS = [
  {
    id: "workspace_dashboard",
    title: "Dedicated Workspace & Folders",
    caption: "Every deck lives in your own org workspace — Apex Robotics Engineering organizes reviews into drag-and-drop folders, just like filing documents.",
    url: "vybemeter.app/workspace/apex-robotics/dashboard",
    img: "/screenshots/workspace_dashboard.png",
    span: "lg:col-span-7",
  },
  {
    id: "slide_editor",
    title: "Interactive Slide Editor",
    caption: "Build polls, reorder questions with drag-and-drop, and configure timers & speed scoring.",
    url: "vybemeter.app/editor/q3-sensor-calibration",
    img: "/screenshots/slide_editor.png",
    span: "lg:col-span-5",
  },
  {
    id: "live_present_stage",
    title: "Cyber-Glass Live Stage",
    caption: "Glowing glass charts — including speedometer gauges — update the instant a vote comes in, presented full-screen for the whole room to see.",
    url: "vybemeter.app/present/571133",
    live: "gauge",
    span: "lg:col-span-12",
  },
  {
    id: "live_reactions",
    title: "Live Emoji Reactions",
    caption: "Participants tap a reaction and it floats up across the big screen in real time — instant, wordless feedback that keeps every room energized.",
    url: "vybemeter.app/present/571133",
    img: "/screenshots/live_reactions.png",
    span: "lg:col-span-6",
  },
  {
    id: "share_modal",
    title: "Editor & Viewer Sharing",
    caption: "Grant teammates Editor access to build slides, or Viewer access to present — owners and admins stay in control.",
    url: "vybemeter.app/workspace/apex-robotics/share",
    img: "/screenshots/share_modal.png",
    span: "lg:col-span-6",
  },
  {
    id: "regional_analytics",
    title: "Regional Analytics Map",
    caption: "An auto-zooming map of every session by location — click a pin to drill down into that session's results.",
    url: "vybemeter.app/workspace/apex-robotics/analytics",
    img: "/screenshots/regional_analytics.png",
    span: "lg:col-span-6",
  },
  {
    id: "bulk_import",
    title: "Bulk Import from Excel",
    caption: "Download a template, fill in questions with dropdown-guided options, and import an entire survey in one upload.",
    url: "vybemeter.app/workspace/import",
    img: "/screenshots/bulk_import.png",
    span: "lg:col-span-6",
  },
  {
    id: "team_invites",
    title: "Team Invites & Roles",
    caption: "Invite teammates to build and present decks together under one shared organization workspace.",
    url: "vybemeter.app/workspace/apex-robotics/team",
    img: "/screenshots/team_invites.png",
    span: "lg:col-span-6",
  },
  {
    id: "branding_studio",
    title: "Logo, Branding & Personalization",
    caption: "Upload your logo, set brand colors and fonts, and preview exactly how the presenter stage will look.",
    url: "vybemeter.app/workspace/apex-robotics/branding",
    img: "/screenshots/branding_studio.png",
    span: "lg:col-span-6",
  },
  {
    id: "org_analytics",
    title: "Organization Admin Analytics",
    caption: "Track users, presentations, and engagement trends across your whole team from one dashboard.",
    url: "vybemeter.app/workspace/apex-robotics/analytics-overview",
    img: "/screenshots/org_analytics.png",
    span: "lg:col-span-6",
  },
];

function LiveGaugeStage() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2200);
    return () => clearInterval(id);
  }, []);
  const values = [62, 74, 81, 88, 79, 91];
  const pct = values[tick % values.length];
  const responses = 18 + tick * 3;
  return (
    <div className="w-full h-full stage-radial flex flex-col items-center justify-center px-6 py-5" data-testid="live-gauge-stage">
      <p className="text-white font-heading font-bold text-center leading-snug mb-1 text-sm sm:text-lg">
        Did you enjoy the training program today?
      </p>
      <div className="w-full max-w-[280px]">
        <GaugeChart value={pct} min={0} max={100} displayValue={`${pct}%`} label="Yes, a lot" dark />
      </div>
      <p className="text-white/40 text-xs -mt-1">{responses} responses</p>
    </div>
  );
}

function BrowserFrame({ shot }) {
  return (
    <div className={`relative ${shot.span}`} data-testid={`screenshot-card-${shot.id}`}>
      <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/10 via-purple-500/5 to-orange-500/10 rounded-3xl blur-2xl -z-10 pointer-events-none" />
      <div className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_20px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_25px_60px_rgba(79,70,229,0.12)] transition-all duration-300 overflow-hidden">
        <div className="h-10 bg-slate-100/90 border-b border-slate-200 px-4 flex items-center gap-3 text-xs font-mono text-slate-500 select-none">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="truncate">{shot.url}</span>
        </div>
        <div style={{ aspectRatio: shot.span.includes("12") ? "16/7" : "16/10" }}>
          {shot.live === "gauge" ? (
            <LiveGaugeStage />
          ) : (
            <img src={shot.img} alt={shot.title} loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
          )}
        </div>
      </div>
      <div className="mt-4">
        <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-1">{shot.title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed">{shot.caption}</p>
      </div>
    </div>
  );
}

export function VybeInActionGallery() {
  return (
    <section className="px-6 max-w-7xl mx-auto pb-28" data-testid="section-vybe-in-action">
      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#FF5733] bg-[#FF5733]/10 px-3 py-1.5 rounded-full mb-4">
        Real product, real screens
      </span>
      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">See VybeMeter in action</h2>
      <p className="text-slate-600 max-w-2xl mb-12">Explore the actual platform across workspace management, live editing, stage mode, analytics, and admin controls.</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {SHOTS.map((shot) => <BrowserFrame key={shot.id} shot={shot} />)}
      </div>
    </section>
  );
}
